import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { shellBranding, siteName } from "@/lib/branding";
import { type ProductLabels, productLabels } from "@/lib/product-labels";

import { http } from "../client";
import { keys } from "../keys";
import type { Ok, TenantBranding, TenantSettings, TenantSettingsInput } from "../types";
import { useSession } from "./auth";

/** This host's branding: from the session once loaded, else what the page shell carried. */
export function useBranding(): TenantBranding {
  const tenant = useSession().data?.tenant;
  const [shell] = useState(shellBranding);
  return tenant ?? shell;
}

/** The tenant admin's organization settings. */
export function useTenantSettings(enabled = true) {
  return useQuery({
    queryKey: keys.tenantSettings,
    queryFn: ({ signal }) => http.get<TenantSettings>("/tenant/settings", signal),
    enabled,
  });
}

/** Settings changes may show elsewhere (the session carries the branding): refresh it too. */
function useTenantMutation<TInput>(fn: (input: TInput) => Promise<TenantSettings>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: async (settings) => {
      queryClient.setQueryData(keys.tenantSettings, settings);
      await queryClient.invalidateQueries({ queryKey: keys.session });
    },
  });
}

export function useUpdateTenant() {
  return useTenantMutation((input: TenantSettingsInput) => http.patch<TenantSettings>("/tenant/settings", input));
}

export function useAddContentAdmin() {
  return useTenantMutation((email: string) => http.post<TenantSettings>("/tenant/content-admins", { email }));
}

export function useRemoveContentAdmin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: number) => http.delete<Ok>(`/tenant/content-admins/${userId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.tenantSettings }),
  });
}

/** The suite's names on this host: "Hume Learn" and "Hume ID" on the root site, never "Hume" on a tenant's. */
export function useProductLabels(): ProductLabels {
  return productLabels(useBranding());
}

/** The site's name: the tenant's on its hosts (white label), else the product's (settings.PRODUCT_NAME). */
export function useBrandName(): string {
  return siteName(useBranding());
}
